import { Injectable } from '@angular/core'

import { INSTITUTIONAL_ASSET_MANAGEMENT_IAM_FAS } from '../../../constants/constant.content'
import { IProduct } from '../../models/product'
import { UtilityService } from '../utility-service/utility-service.service'

@Injectable({
  providedIn: 'root',
})
export class ClassificationRulesService {
  public static isRelevant(product: IProduct): boolean {
    let found = false
    if (product && product.clientGroup) {
      product.clientGroup.forEach((clientGrpName) => {
        if (
          UtilityService.wordMatch(
            INSTITUTIONAL_ASSET_MANAGEMENT_IAM_FAS,
            clientGrpName,
          ) &&
          !product.fundShortName.startsWith('VVIF')
        ) {
          found = true
        }
      })
    }
    return found
  }

  static isFixedIncome(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      (UtilityService.wordMatch(product.assetClassificationLevel1, 'bond') ||
        (UtilityService.wordMatch(product.investmentStrategy, 'Stable Value') &&
          UtilityService.wordMatch(
            product.productTypeLevel2,
            'Collective Investment Trust (CIT)',
          )))
    )
  }

  static isEquity(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.assetClassificationLevel1, 'equity')
    )
  }
  // eslint-disable-next-line complexity
  public static isStableFund(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      product.productTypeLevel2 === 'Collective Investment Trust (CIT)' &&
      (product.investmentStrategy === 'Stable Value' ||
        (product.managementStrategy === 'Index' &&
          product.fundIndicator === 'Fund of Fund'))
    )
  }

  // Multi-Asset
  static isMultiAsset(product: IProduct) {
    return (
      (ClassificationRulesService.isRelevant(product) &&
        UtilityService.wordMatch(
          product.investmentStrategy,
          'Life Strategy',
        )) ||
      ClassificationRulesService.isTargetDateMultiAsset(product) ||
      ClassificationRulesService.isTargetAllocationMultiAsset(product) ||
      ClassificationRulesService.isBalancedMultiAsset(product)
    )
  }
  static isRTF(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      product.fundFullName.toUpperCase().indexOf('TARGET RETIREMENT') >= 0
    )
  }
  static isUSMultiAsset(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isMultiAsset(product) &&
      UtilityService.wordMatch(
        product.regionOfInvestment,
        'Country of Domicile',
      )
    )
  }

  static isIntlMultiAsset(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isMultiAsset(product) &&
      (UtilityService.wordMatch(product.regionOfInvestment, 'International') ||
        UtilityService.wordMatch(product.regionOfInvestment, 'Global'))
    )
  }

  // eslint-disable-next-line complexity
  static isTargetDateMultiAsset(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.investmentStrategy, 'Target Date')
    )
  }

  static isTargetAllocationMultiAsset(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.investmentStrategy, 'Life Strategy')
    )
  }

  // eslint-disable-next-line complexity
  static isBalancedMultiAsset(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.assetClassificationLevel2, 'Balanced') &&
      !ClassificationRulesService.isTargetAllocationMultiAsset(product) &&
      !ClassificationRulesService.isRTF(product) &&
      (UtilityService.wordMatch(product.assetClassificationLevel1, 'bond') ||
        UtilityService.wordMatch(
          product.assetClassificationLevel1,
          'Balanced/Diversified',
        )) &&
      (UtilityService.wordMatch(product.regionOfInvestment, 'International') ||
        UtilityService.wordMatch(product.regionOfInvestment, 'Global') ||
        UtilityService.wordMatch(
          product.regionOfInvestment,
          'Country of Domicile',
        ))
    )
  }

  // Cash Equivalent
  static isCashEquivalent(product: IProduct) {
    return (
      (ClassificationRulesService.isRelevant(product) &&
        (UtilityService.wordMatch(
          product.assetClassificationLevel2,
          'Money Market',
        ) ||
          UtilityService.wordMatch(
            product.assetClassificationLevel1,
            'Short-term Reserves',
          ))) ||
      (UtilityService.wordMatch(product.investmentStrategy, 'Stable Value') &&
        UtilityService.wordMatch(
          product.productTypeLevel2,
          'Collective Investment Trust (CIT)',
        ))
    )
  }
  static isTaxableCashEquivalent(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isCashEquivalent(product) &&
      UtilityService.wordMatch(product.industryCategory, 'Taxable Money Market')
    )
  }

  static isMuniCashEquivalent(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      ClassificationRulesService.isCashEquivalent(product) &&
      (ClassificationRulesService.isStateMuniCashEquivalent(product) ||
        ClassificationRulesService.isNationalMuniCashEquivalent(product))
    )
  }

  static isStateMuniCashEquivalent(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(
        product.industryCategory,
        'Muni Single State Money Market',
      )
    )
  }

  static isNationalMuniCashEquivalent(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(
        product.industryCategory,
        'Muni National Money Market',
      )
    )
  }

  static isStableFundCashEquivalent(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      product.investmentStrategy === 'Stable Value' &&
      product.productTypeLevel2 === 'Collective Investment Trust (CIT)' &&
      ClassificationRulesService.isCashEquivalent(product)
    )
  }

  // Strategy
  static isDividendStrategy(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      product.foiTypes &&
      UtilityService.existAnyWordMatch(product.foiTypes, 'dividend')
    )
  }

  static isESGStrategies(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(
        product.investmentStrategy,
        'Environmental, Social and Governance (ESG)',
      ) &&
      !UtilityService.wordMatch(
        product.productTypeLevel2,
        'Donor Advised Funds',
      )
    )
  }

  static isFactorDrivenStrategies(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      UtilityService.wordMatch(product.investmentStrategy, 'Factor-based')
    )
  }

  static isAlternativeSplLevel1(product: IProduct) {
    return (
      ClassificationRulesService.isRelevant(product) &&
      (UtilityService.wordMatch(product.investmentStrategy, 'Sector Fund') ||
        UtilityService.wordMatch(
          product.assetClassificationLevel1,
          'Alternatives',
        ) ||
        UtilityService.wordMatch(product.industryCategory, 'Long-Short'))
    )
  }
}
